import { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../config';


/**
 * Check if a PRL document has been acknowledged by a user
 * @param {string} token the auth token
 * @param {string} uid the user ID
 * @param {string} did the PRL document ID
 * @param {string} wid the work ID
 * @return {Object} the acknowledge status
 */
const useCheckAcknowledge = (token, uid, did, wid = null) => {
    const [ack, setAck] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchAck = async (resetLoading = false) => {
        try {
            if (resetLoading) {
                setLoading(true);
                setError(null);
            }

            let url = `${API_BASE_URL}/prldocuments/user/ack/${uid}/${did}`;
            if (wid) {
                url += `?wid=${wid}`;
            }

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error('Network response was not ok');
            }

            const data = await response.json();
            setAck(data);
        } catch (err) {
            setError(err);
            setAck(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!ack && token) {
            fetchAck();
        }
    }, [token, uid, did]);

    const refetch = () => fetchAck(true);

    return { ack, loading, error, refetch };
};

/**
 * Mark a PRL document as acknowledged
 * @param {string} token the auth token
 * @param {string} uid the user ID
 * @param {string} did the PRL document ID
 * @param {Object} body the body of the request (optional)
 * @return {Promise<Response>} the response from the API
 */
const markPrlDocumentAsAcknowledged = async (token, uid, did, body = {}) => {
    return await fetch(`${API_BASE_URL}/prldocuments/user/ack/${uid}/${did}`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(body),
    });
};

export { useCheckAcknowledge, markPrlDocumentAsAcknowledged };